import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { RoleContext } from './context';
import { STORAGE_KEYS } from 'utils/constants';
import { ENUM_ALL_ROLES } from 'utils/roles';
import {
  InventoryModule,
  PermissionType,
  RoleAuthorizationState,
  RolePermission
} from 'models/permission.model';
import {
  buildPermissionIndex,
  canWithIndex,
  hasModuleAccess as hasModulePermission,
  normalizePermissions,
  readStoredRole,
  StoredRole,
  writeStoredRole,
  readStoredPermissions
} from 'utils/authorization';

const RoleProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [role, setRoleState] = useState<StoredRole | undefined>(() => {
    const storedRole = readStoredRole();
    const initialPermissions = normalizePermissions(
      storedRole?.permissions?.length ? storedRole.permissions : readStoredPermissions()
    );
    if (!storedRole?.name && !storedRole?.id && !initialPermissions.length) {
      return undefined;
    }
    return {
      name: storedRole?.name,
      id: storedRole?.id,
      permissions: initialPermissions
    };
  });
  const [permissions, setPermissionsState] = useState<PermissionType[]>(() =>
    normalizePermissions(
      readStoredRole()?.permissions?.length
        ? readStoredRole()?.permissions
        : readStoredPermissions()
    )
  );
  const [isMainAdmin, setIsMainAdmin] = useState<boolean>(() => {
    const adminRole = localStorage.getItem(STORAGE_KEYS.ADMIN_ROLE) as ENUM_ALL_ROLES;
    return [ENUM_ALL_ROLES.ADMIN, ENUM_ALL_ROLES.SUPER_ADMIN].includes(adminRole);
  });

  const setRole = useCallback(
    (nextRole?: StoredRole) => {
      if (
        !nextRole?.name &&
        !nextRole?.id &&
        !(nextRole?.permissions && nextRole.permissions.length)
      ) {
        setRoleState(undefined);
        setPermissionsState([]);
        writeStoredRole();
        return;
      }

      const normalizedPermissions = normalizePermissions(
        nextRole.permissions ?? role?.permissions ?? permissions
      );
      const mergedRole: StoredRole = {
        name: nextRole.name ?? role?.name,
        id: nextRole.id ?? role?.id,
        permissions: normalizedPermissions
      };
      setRoleState(mergedRole);
      setPermissionsState(normalizedPermissions);
      writeStoredRole({
        name: mergedRole.name,
        id: mergedRole.id,
        permissions: normalizedPermissions
      });
    },
    [permissions, role]
  );

  const setPermissions = useCallback((nextPermissions: PermissionType[] = []) => {
    const normalizedPermissions = normalizePermissions(nextPermissions);
    setPermissionsState(normalizedPermissions);
    setRoleState((prevRole) => {
      const nextRole: StoredRole | undefined =
        prevRole?.name || prevRole?.id || normalizedPermissions.length
          ? {
              name: prevRole?.name,
              id: prevRole?.id,
              permissions: normalizedPermissions
            }
          : undefined;
      writeStoredRole(nextRole);
      return nextRole;
    });
  }, []);

  const clearAuthorization = useCallback(() => {
    setRoleState(undefined);
    setPermissionsState([]);
    writeStoredRole();
  }, []);

  const setRoleAuthorization = useCallback(
    (authorization?: Partial<RoleAuthorizationState>) => {
      if (!authorization) {
        clearAuthorization();
        return;
      }

      const { roleName, roleId: nextRoleId, permissions: nextPermissions } = authorization;
      const normalizedPermissions = normalizePermissions(nextPermissions ?? []);
      const nextRole: StoredRole = {
        name: roleName,
        id: nextRoleId,
        permissions: normalizedPermissions
      };
      setRoleState(nextRole);
      setPermissionsState(normalizedPermissions);
      writeStoredRole(nextRole);
    },
    [clearAuthorization]
  );

  const permissionIndex = useMemo(() => buildPermissionIndex(permissions), [permissions]);

  const can = useCallback(
    (
      module: InventoryModule | string,
      actions: RolePermission | RolePermission[] = RolePermission.Read
    ) => {
      if (isMainAdmin) return true;
      return canWithIndex(permissionIndex, module, actions);
    },
    [isMainAdmin, permissionIndex]
  );

  const hasModuleAccess = useCallback(
    (module: InventoryModule | string) => {
      if (isMainAdmin) return true;
      return hasModulePermission(permissions, module);
    },
    [isMainAdmin, permissions]
  );

  return (
    <RoleContext.Provider
      value={{
        role,
        permissions,
        setRole,
        setPermissions,
        setRoleAuthorization,
        clearAuthorization,
        can,
        hasModuleAccess,
        isMainAdmin,
        setIsMainAdmin
      }}>
      {children}
    </RoleContext.Provider>
  );
};

export default RoleProvider;
